import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Button, Space, Typography, Descriptions, Tag, notification, Popconfirm } from 'antd';
import { FolderOpenOutlined, CalculatorOutlined, FileTextOutlined, ScanOutlined, SettingOutlined, ApartmentOutlined, BankOutlined, TeamOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../authentication/AuthProvider';
import request from '../lib/request';

/** The "Accounting" tab of a client: its books (xlsx MENU "Accounting -> Select a Client"), or the button to open them. */
export default function ClientBooksCard({ client, onChanged }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const canEdit = auth.user.role === 'SYSTEM_ADMIN' || auth.user.role === 'COMPANY_MANAGER';
  const [opening, setOpening] = React.useState(false);
  const book = client.Ledger;

  const open = () => {
    setOpening(true);
    request
      .post('/ledgers', { client_id: client.id, company_id: client.company_id })
      .then((r) => r.json())
      .then((r) => {
        if (!r.status) return notification.error({ message: r.message });
        notification.success({ message: r.message });
        onChanged();
      })
      .finally(() => setOpening(false));
  };
  const go = (path) => navigate(`${path}${path.includes('?') ? '&' : '?'}ledger=${book.id}`);

  if (!book) {
    return (
      <Card size="small" title="Books">
        <Typography.Paragraph type="secondary">
          No books have been opened for {client.client_name} yet. Opening them creates the client's own chart of accounts, transaction codes, Cyprus VAT codes and VAT form lines from the ADMIN template; postings, VAT returns, reports and the asset register then live under this client.
        </Typography.Paragraph>
        {canEdit ? (
          <Popconfirm title={`Open books for ${client.client_name}?`} onConfirm={open}>
            <Button type="primary" icon={<FolderOpenOutlined />} loading={opening}>Open books</Button>
          </Popconfirm>
        ) : (
          <Typography.Text type="secondary">Ask a manager to open the books.</Typography.Text>
        )}
      </Card>
    );
  }

  return (
    <Card size="small" title={<span>Books <Tag color={book.status === 'ACTIVE' ? 'green' : 'default'}>{book.status}</Tag></span>} extra={<Link to={`/books?ledger=${book.id}`}><SettingOutlined /> Books settings</Link>}>
      <Descriptions size="small" column={{ xs: 1, md: 3 }} style={{ marginBottom: 12 }}>
        <Descriptions.Item label="Name">{book.name}</Descriptions.Item>
        <Descriptions.Item label="Currency">{book.base_currency}</Descriptions.Item>
        <Descriptions.Item label="Financial year starts">{moment.months()[(book.financial_year_start_month || 1) - 1]}</Descriptions.Item>
        <Descriptions.Item label="Opened">{book.created_at ? moment(book.created_at).format('DD MMM YYYY') : '-'}</Descriptions.Item>
      </Descriptions>
      <Space wrap>
        <Button icon={<FolderOpenOutlined />} type="primary" onClick={() => go('/journal')}>Transaction posting</Button>
        <Button icon={<TeamOutlined />} onClick={() => go('/counterparties')}>Customers / Suppliers</Button>
        <Button icon={<CalculatorOutlined />} onClick={() => go('/ledger-reports?tab=tb')}>Ledger reports</Button>
        <Button icon={<FileTextOutlined />} onClick={() => go('/vat-reports')}>VAT / VIES / OSS</Button>
        <Button icon={<ApartmentOutlined />} onClick={() => go('/chart-of-accounts')}>Chart of accounts</Button>
        <Button icon={<BankOutlined />} onClick={() => go('/fixed-assets')}>Fixed assets</Button>
        <Button icon={<CalculatorOutlined />} onClick={() => go('/budgets')}>Budgeting</Button>
        <Button icon={<ScanOutlined />} onClick={() => go('/scan-n-read')}>ScanNRead documents</Button>
      </Space>
    </Card>
  );
}
